import { apiRequest } from "../../../../shared/lib/api";
import {
  CreateOpenMatchPayload,
  OpenMatchInitialParticipant,
  OpenMatchJoinResponse,
  OpenMatchMessagesResponse,
  OpenMatchParticipantStatus,
  PublicOpenMatchResponse,
  PublicOpenMatchesResponse,
} from "../types/matches";

export function listPublicMatches() {
  return apiRequest<PublicOpenMatchesResponse>("/api/public/open-matches");
}

export function getPublicMatch(matchId: number) {
  return apiRequest<PublicOpenMatchResponse>(
    `/api/public/open-matches/${encodeURIComponent(String(matchId))}`,
  );
}

export function getAuthenticatedMatch(token: string, matchId: number) {
  return apiRequest<PublicOpenMatchResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function listMyMatches(token: string) {
  return apiRequest<PublicOpenMatchesResponse>("/api/open-matches/mine", {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function requestJoinMatch(token: string, matchId: number) {
  return apiRequest<OpenMatchJoinResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}/join`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function cancelMyMatchParticipant(token: string, matchId: number) {
  return apiRequest<OpenMatchJoinResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}/participants/me/cancel`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function cancelMatch(token: string, matchId: number) {
  return apiRequest<PublicOpenMatchResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}/cancel`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function confirmMatch(token: string, matchId: number) {
  return apiRequest<PublicOpenMatchResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}/confirm`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function inviteMatchParticipant(
  token: string,
  matchId: number,
  userId: number,
) {
  return apiRequest<PublicOpenMatchResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}/invites`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ userId }),
    },
  );
}

export function addMatchParticipant(
  token: string,
  matchId: number,
  payload: OpenMatchInitialParticipant,
) {
  return apiRequest<PublicOpenMatchResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}/participants`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    },
  );
}

export function createMatchMessage(
  token: string,
  matchId: number,
  message: string,
) {
  return apiRequest<OpenMatchMessagesResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}/messages`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ message }),
    },
  );
}

export function listMatchMessages(token: string, matchId: number) {
  return apiRequest<OpenMatchMessagesResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}/messages`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function markMatchChatRead(token: string, matchId: number) {
  return apiRequest<OpenMatchMessagesResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}/chat/read`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function deleteMatchMessage(
  token: string,
  matchId: number,
  messageId: number,
) {
  return apiRequest<OpenMatchMessagesResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}/messages/${encodeURIComponent(String(messageId))}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function reviewMatchParticipant(
  token: string,
  matchId: number,
  participantId: number,
  status: OpenMatchParticipantStatus,
) {
  return apiRequest<PublicOpenMatchResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}/participants/${encodeURIComponent(String(participantId))}`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    },
  );
}

export function createMatch(token: string, payload: CreateOpenMatchPayload) {
  return apiRequest<PublicOpenMatchResponse>("/api/open-matches", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export function updateMatch(
  token: string,
  matchId: number,
  payload: CreateOpenMatchPayload,
) {
  return apiRequest<PublicOpenMatchResponse>(
    `/api/open-matches/${encodeURIComponent(String(matchId))}`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    },
  ).catch((error) => {
    const message = error instanceof Error ? error.message : "";
    const normalizedMessage = String(message || "")
      .trim()
      .toLowerCase();
    const missingRoute =
      normalizedMessage.includes("cannot patch /api/open-matches/") ||
      normalizedMessage === "recurso não encontrado." ||
      normalizedMessage === "resource not found.";

    if (missingRoute) {
      throw new Error(
        "A edição de partidas foi adicionada recentemente. Reinicie o backend local (`npm run server`) para carregar a nova rota e tente novamente.",
      );
    }

    throw error;
  });
}
